import { InputSelect } from '../../components/forms/inputs/InputSelect';
import { Button } from '../../components/buttons/Button';
import { useEffect, useRef, useState, Fragment } from "react";
import { postData, useGetData } from "../../services/api/api-tools";
import LogoPerception from '../../assets/images/logos/logo-blue-black.png';
import LogoLCA from '../../assets/images/logos/LCA_Logo.jpg';
import { useSocket } from "../../contexts/SocketContext";
import {
  ArrowUturnLeftIcon,
  CheckIcon,
  ExclamationCircleIcon, FolderIcon,
  InformationCircleIcon, PlayIcon, StopIcon, XCircleIcon,
  XMarkIcon
} from "@heroicons/react/20/solid";
import { useAlert } from "../../contexts/AlertContext";
import { Timeline } from "./components/Timeline";
import { PHASE_CHECK, PHASES_TEXTS } from "../../services/live-interface/lca-interface-service";
import { PatternData } from "./components/PatternData";
import { useMemo } from "react";
import { NewPiecesNumberModal } from "./components/NewPiecesNumberModal";

export const LCALivePage = () => {

  const { socket, subscribeToEvent, unsubscribeFromEvent, sendEvent } = useSocket();
  const { showAlert } = useAlert();

  const { data: patterns } = useGetData('patterns', `patterns`);

  const [selectedPattern, setSelectedPattern] = useState(null);
  const [isStarted, setIsStarted] = useState(false);
  const [phases, setPhases] = useState([]);
  const currentPhase = phases.length > 0 ? phases[phases.length - 1] : null;
  const [currentData, setCurrentData] = useState({});
  const [showSynthesis, setShowSynthesis] = useState(false);
  const [synthesisDate, setSynthesisDate] = useState(null);

  /* New pieces number detected modal */
  const [isNewPiecesNumberModalOpen, setIsNewPiecesNumberModalOpen] = useState(false);
  const [newPiecesNumberData, setNewPiecesNumberData] = useState({});

  const lockedData = useRef(false);

  const currentComment = useMemo(() => {
    if (!currentPhase) return null;

    for (const patternPhase of selectedPattern.pattern_phases) {
      if (patternPhase.type === currentPhase.type) {
        return patternPhase.comment;
      }
    }

    return null;
  }, [currentPhase]);

  const remainingTimeForStrass = useMemo(() => {
    for (const phase of phases){
      if (phase.type === 1) {
        return Math.max(Math.floor(20 * 60 - (new Date().getTime() / 1000 - phase.start_date)), 0);
      }
    }
    return null;
  }, [phases]);

  const handleStart = () => {
    if(selectedPattern !== null)
      setIsStarted(true);

    /* Send pattern data to the engine */
    const patternDataToSend = selectedPattern.pattern_data.reduce((acc, { name, value }) => {
      acc[name] = value;
      return acc;
    }, {});
    sendEvent('selected_pattern', patternDataToSend);
  };

  const resetInterface = () => {
    // This check is needed to prevent from resetting the interface twice
    if (showSynthesis && synthesisDate !== null) {
      setPhases([]);
      setCurrentData([]);
      lockedData.current = false;
      setShowSynthesis(false);
      setSynthesisDate(null);
      setNewPiecesNumberData({});
      handleStart();
    }
  };

  useEffect(() => {
    if (!isStarted) return;
    if (!socket) return;

    const handleReceivedCurrentPhase = (currentPhaseData) => {
      if (lockedData.current) return;

      // Detect process end
      if (currentPhaseData?.phase !== -1){
        const eventDate = new Date().getTime() / 1000;

        setPhases(prevPhases => {
          const lastPhase = prevPhases.length > 0 ? prevPhases[prevPhases.length - 1] : null;

          // First detected phase or new phase
          if (lastPhase === null || lastPhase.type !== currentPhaseData.phase) {
            return [...prevPhases, {type: currentPhaseData.phase, start_date: eventDate, end_date: eventDate}];
          }
          // Continuing the same phase
          else{
            return prevPhases.map((phase, index) => index === prevPhases.length - 1 ? { ...phase, end_date: eventDate } : phase);
          }
        });
      }
      else{
        stopProcess();
      }
    };

    const handleReceivedData = (data) => {
      if (lockedData.current) return;
      setCurrentData(prevData => ({...prevData, ...data}));

      if (selectedPattern && isStarted && data?.pieces_number) {
        const originalPiecesNumber = selectedPattern.pattern_data.find((data) => data.name === 'pieces_number');
        if (originalPiecesNumber !== undefined) {
          setNewPiecesNumberData({ pieces_number: data.pieces_number });
          setIsNewPiecesNumberModalOpen(true);
        }
      }
    };

    subscribeToEvent('current_phase', handleReceivedCurrentPhase);
    subscribeToEvent('current_data', handleReceivedData);

    return () => {
      unsubscribeFromEvent('current_phase', handleReceivedCurrentPhase);
      unsubscribeFromEvent('current_data', handleReceivedData);
    };
  }, [socket, isStarted]);

  const getPhasesTransitions = (phases) => {
    if (phases === null || phases === undefined || phases.length === 0) return [];
    const transitions = [];

    const filteredPhases = [{'type': -2}, ...phases.filter((phase) => phase.type !== 0)];

    for (let i = 1; i < filteredPhases.length; i++) {
      const prev = filteredPhases[i - 1].type;
      const curr = filteredPhases[i].type;
      if (prev !== curr && prev !== 0 && curr !== 0) {
        transitions.push({ from: prev, to: curr });
      }
    }
    return transitions;
  };


  const patternTransitions = getPhasesTransitions(selectedPattern?.pattern_phases);
  const transitions = getPhasesTransitions(phases);

  const phaseErrorIndex = useMemo(() => {
    let index = null;

    for (let i=0; i<transitions.length; i++) {
      if (i > patternTransitions.length - 1)
        index = i; // +1 already considered with first fake transition from -2

      else if (transitions[i].from !== patternTransitions[i].from || transitions[i].to !== patternTransitions[i].to)
        index = i; // +1 already considered with first fake transition from -2

      if (index)
        break;
    }

    return index;
  }, [patternTransitions, transitions]);

  const handleRaiseError = () => {
    console.log('New error raised.');
    postData('live_errors', {date: Math.floor(Date.now() / 1000), pattern: `/api/patterns/${selectedPattern?.id}`});
    showAlert('success', 'Le manquement a bien été remonté.')
  };

  const handleCloseSynthesis = (e) => {
    e.stopPropagation();
    resetInterface();
  };

  const stopProcess = () => {
    lockedData.current = true;
    setSynthesisDate((new Date()).getTime() / 1000);
    setShowSynthesis(true);
    setTimeout(resetInterface, 30000);
  };

  const handleConfirmNewPiecesNumber = () => {
    sendEvent('selected_pattern', { pieces_number: newPiecesNumberData.pieces_number });
  };

  const handleContinue = () => {
    const now = (new Date()).getTime() / 1000;
    setPhases(prevPhases => [...prevPhases, {type: PHASE_CHECK, start_date: synthesisDate, end_date: now}]);
    setSynthesisDate(null);
    setShowSynthesis(false);
    lockedData.current = false;
  };

  return (
    <div className='h-full overflow-hidden bg-gray-100'>
      <NewPiecesNumberModal
        isOpen={isNewPiecesNumberModalOpen}
        setIsOpen={setIsNewPiecesNumberModalOpen}
        data={newPiecesNumberData}
        onConfirm={handleConfirmNewPiecesNumber}
        onCancel={() => window.location.reload()}
      />

      <div className='flex flex-col items-center justify-center w-full h-full'>
        {
          isStarted && selectedPattern !== null ?
            <div className="w-full h-full flex flex-col justify-around">

              {/* Top part */}
              <div className="w-full h-3/5 flex justify-around p-4 gap-4">
                {/* Left column */}
                <div className="w-7/12 flex flex-col gap-4">
                  {/* Header with selected pattern name */}
                  <div className="bg-perception-blue p-4 text-white text-center relative rounded-md">
                    <div className="absolute top-0 h-full left-0 flex justify-center">
                      <button className="text-white p-4 rounded-sm flex items-center" onClick={() => window.location.reload()}>
                        <ArrowUturnLeftIcon aria-hidden="true" className="size-5 mr-2" /> Retour
                      </button>
                    </div>

                    <p className="text-xl">Gamme sélectionnée :</p>
                    <p className="text-3xl">
                      {selectedPattern.name}
                    </p>
                  </div>

                  {/* Pattern data */}
                  <div className="flex h-full justify-between items-center gap-4">
                    <div className="w-1/2 overflow-y-auto h-full bg-gray-200 rounded-md">
                      <PatternData patternData={selectedPattern.pattern_data} currentData={currentData}
                                   remainingTimeForStrass={remainingTimeForStrass} newPiecesNumberData={newPiecesNumberData}/>
                    </div>
                    <div className="w-1/2 bg-white h-full flex flex-col justify-center items-center rounded-md">
                      <FolderIcon className='w-24 h-24 text-gray-200'/>
                      <div className='text-gray-300 text-2xl mt-4'>Schéma strassage (à venir)</div>
                    </div>
                  </div>
                </div>

                {/* Right column */}
                <div className="w-5/12 flex flex-col gap-4">
                  {/* Current step */}
                  <div className="bg-gray-200 p-16 text-center relative rounded-md">
                    <p className="text-xl">Etape en cours :</p>
                    <p className="text-5xl">
                      {
                        currentPhase !== null ? PHASES_TEXTS[currentPhase.type] : 'Aucune'
                      }
                    </p>

                    <div className="absolute top-4 left-4 flex justify-center">
                      <button className="bg-perception-blue text-white p-4 rounded-md flex items-center" onClick={handleStart}>
                        <PlayIcon aria-hidden="true" className="size-5 mr-2" /> Démarrer
                      </button>
                    </div>

                    <div className="absolute top-4 right-4 flex justify-center">
                      <button className="bg-error text-white p-4 rounded-md flex items-center" onClick={stopProcess}>
                        <StopIcon aria-hidden="true" className="size-5 mr-2" /> STOP
                      </button>
                    </div>
                  </div>

                  {/* Commentary */}
                  <div className="h-full text-center">
                    {
                      currentData?.alert ?
                        <div className="h-full rounded-md bg-red-50 p-4 flex text-left items-center">
                          <div className="shrink-0">
                            <XCircleIcon aria-hidden="true" className="size-8 text-red-400" />
                          </div>
                          <div className="ml-3">
                            <h3 className="text-xl font-medium text-red-800">
                              {currentData.alert.split('\n').map((line, index) => (
                                <Fragment key={index}>{line}<br/></Fragment>
                              ))}
                            </h3>
                          </div>
                        </div>
                      : currentData?.info ?
                        <div className="h-full rounded-md p-4 flex text-left items-center">
                          <div className="shrink-0">
                            <InformationCircleIcon aria-hidden="true" className="size-8 text-blue-400" />
                          </div>
                          <div className="ml-3 flex-1 md:flex md:justify-between">
                            <h3 className="text-sm text-blue-700">
                              {currentData.info.split('\n').map((line, index) => (
                                <Fragment key={index}>{line}<br/></Fragment>
                              ))}
                            </h3>
                          </div>
                        </div>
                      : currentComment ?
                        <div className="h-full rounded-md p-4 flex text-left items-center">
                          <div className="shrink-0">
                            <InformationCircleIcon aria-hidden="true" className="size-8 text-blue-400" />
                          </div>
                          <div className="ml-3 flex-1 md:flex md:justify-between">
                            <h3 className="text-sm text-blue-700">
                              {currentComment.split('\n').map((line, index) => (
                                <Fragment key={index}>{line}<br/></Fragment>
                              ))}
                            </h3>
                          </div>
                        </div>
                      :
                        <div className='h-full flex flex-col justify-center items-center text-center'>
                          <InformationCircleIcon className='w-24 h-24 text-gray-300'/>
                          <div className='text-gray-500'>Pas d'informations à afficher</div>
                        </div>
                    }
                  </div>

                  {/*<div className="flex justify-center">*/}
                  {/*  <button className="bg-error text-white p-4 rounded-sm" onClick={handleRaiseError}>Déclarer un manquement</button>*/}
                  {/*</div>*/}
                </div>
              </div>

              {/* Timeline */}
              <div className="h-2/5 py-12">
                <Timeline pattern={selectedPattern} phases={phases} phaseErrorIndex={phaseErrorIndex} isLive={true} />
              </div>

              {/* Logos */}
              <div className="pb-4 flex justify-center items-center text-2xl gap-4">
                <img src={LogoPerception} alt="logo-perception" className="max-w-[200px] max-h-[60px]" />
                x
                <img src={LogoLCA} alt="logo-LCA" className="max-w-[200px] max-h-[60px]" />
              </div>
            </div>
            :
            <>
              <h2 className='my-6 text-3xl font-semibold'>Choisir une gamme</h2>
              <div className='flex flex-col gap-4 w-80 '>
                <InputSelect
                  className='py-3'
                  items={patterns}
                  onChange={(pattern) => setSelectedPattern(pattern)}
                  itemToString={(pattern) => pattern?.name}
                  placeholder='Choisir une gamme'
                  category='secondary'
                  autoComplete='off'
                />
              </div>
              <div className='my-6'>
                <Button type='submit' category='primary-btn' size='small' onClick={handleStart}>
                  Démarrer
                </Button>
              </div>
            </>
        }
      </div>

      {
        showSynthesis && isStarted &&
        <div className="absolute top-0 left-0 w-full h-full p-20 bg-black/50 backdrop-blur-sm">
          <div className="relative bg-white h-full rounded-sm flex flex-col justify-between">
            <div className="absolute top-0 right-0 cursor-pointer" onClick={(e) => handleCloseSynthesis(e)}>
              <XMarkIcon className="w-16 h-16 text-gray-600 m-4"/>
            </div>
            <div className="bg-gray-200 flex items-center justify-between pr-24">
              <h1 className="text-center text-3xl p-8">Synthèse de la production</h1>
              <button className="bg-perception-blue text-white p-4 rounded-md flex items-center" onClick={handleContinue}>
                <PlayIcon aria-hidden="true" className="size-5 mr-2" /> Continuer la gamme
              </button>
            </div>

            {/* Main space */}
            <div className="h-full flex flex-col justify-center items-center overflow-auto gap-4">

              {/* Top part */}
              <div className="w-full h-full flex justify-center items-center p-4 gap-4">

                {/* Left column */}
                <div className="w-full max-w-[400px] bg-gray-200 overflow-y-auto h-full">
                  <PatternData patternData={selectedPattern.pattern_data} currentData={currentData} newPiecesNumberData={newPiecesNumberData}/>
                </div>

                {/* Right column */}
                <div className="w-full flex flex-col justify-center items-center text-3xl">
                  {
                    phaseErrorIndex === null ?
                      <>
                        <CheckIcon className="w-36 h-36 text-perception-success-300"/>
                        <p>Aucun problème détecté</p>
                      </>
                      :
                      <div className="flex flex-col justify-around items-center h-full">
                        <div className="flex flex-col items-center">
                          <ExclamationCircleIcon className="w-36 h-36 text-perception-warn-500"/>
                          <p className="text-center">Vérification nécessaire<br/>sur certaines étapes</p>
                        </div>
                      </div>
                  }
                </div>

              </div>

              {/* Bottom part */}
              <div className="w-full h-full overflow-auto p-4">
                {/* Timeline */}
                <Timeline pattern={selectedPattern} phases={phases} phaseErrorIndex={phaseErrorIndex} isLive={false} />
              </div>
            </div>
            <div className="flex justify-center bg-gray-200 p-4">
              <button className="bg-error text-white p-4 rounded-sm" onClick={handleRaiseError}>Déclarer un manquement</button>
            </div>
          </div>
        </div>
      }
    </div>
  );
};
